import React, { useState, useEffect } from 'react';

import { MINIMUM_PAGE_COUNT } from 'constants/page';

import * as userService from 'services/user';
import * as skillV2Service from 'services/skillV2';
import * as designationService from 'services/designation';
import * as designationAreaService from 'services/designationArea';

import useTitle from 'hooks/useTitle';

import Pagination from 'components/common/paginator';
import Loading from 'components/common/loading/Loading';

import { useQuery } from 'utils/queryParam';
import { handleError } from 'utils/errorHandler';

import type { RouterProps } from 'interface/queryParam';

import { ResourceFilter, ResourceResults } from './components';

import type {
  EndLevelSkill,
  DesignationRecord,
  PeopleFinderEmployeeRow,
  ResourceFinderFilterQuery,
  ResourceFinderLoadingState,
} from './types';

const PAGE_COUNT = 10;

const ResourceFinder: React.FC<RouterProps> = (props) => {
  const [query, setQuery] = useQuery(props);
  const [loading, setLoading] = useState<ResourceFinderLoadingState>({
    employees: false,
    skills: false,
    designations: false,
    export: false,
    areas: false,
  });
  const [pageCount, setPageCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [employees, setEmployees] = useState<PeopleFinderEmployeeRow[]>([]);
  const [skills, setSkills] = useState<EndLevelSkill[]>([]);
  const [designations, setDesignations] = useState<DesignationRecord[]>([]);
  const [areas, setAreas] = useState<DesignationRecord[]>([]);

  const pageSize = query.size ? +String(query.size) : PAGE_COUNT;

  const setTitle = useTitle();

  useEffect(() => {
    setTitle('People Finder');
  }, [setTitle]);

  const { page, ...filterQuery } = query;

  useEffect(() => {
    void (async () => {
      try {
        setLoading((prev) => ({
          ...prev,
          skills: true,
        }));

        const { data } = await skillV2Service.fetchEndLevelSkills();

        setSkills(Array.isArray(data) ? (data as EndLevelSkill[]) : []);
      } catch (err) {
        handleError(err as object);
      } finally {
        setLoading((prev) => ({
          ...prev,
          skills: false,
        }));
      }
    })();
  }, []);

  useEffect(() => {
    const fetchDesignations = async () => {
      try {
        setLoading((prevLoading) => ({ ...prevLoading, designations: true }));

        const response = await designationService.fetchAll();

        const { data } = response;
        setDesignations(Array.isArray(data) ? (data as DesignationRecord[]) : []);
      } catch (err) {
        handleError(err as object);
      } finally {
        setLoading((prevLoading) => ({ ...prevLoading, designations: false }));
      }
    };

    fetchDesignations();
  }, []);

  useEffect(() => {
    const fetchAreas = async () => {
      try {
        setLoading((prevLoading) => ({ ...prevLoading, areas: true }));

        const response = await designationAreaService.fetchAll();

        const { data } = response;
        setAreas(Array.isArray(data) ? (data as DesignationRecord[]) : []);
      } catch (err) {
        handleError(err as object);
      } finally {
        setLoading((prevLoading) => ({ ...prevLoading, areas: false }));
      }
    };

    fetchAreas();
  }, []);

  useEffect(() => {
    if (!page) {
      setQuery({ ...query, page: 1 });

      return;
    }

    void (async () => {
      try {
        setLoading((prevLoading) => ({ ...prevLoading, employees: true }));

        const response = await userService.fetchPeopleFinder<PeopleFinderEmployeeRow>(query);

        const { data } = response;
        const { total: totalCount } = response.meta;

        const computedPageCount = Math.ceil(totalCount / pageSize);

        setTotal(totalCount);
        setEmployees(data);
        setPageCount(computedPageCount);
      } catch (err) {
        handleError(err as object);
      } finally {
        setLoading((prevLoading) => ({ ...prevLoading, employees: false }));
      }
    })();
  }, [page, pageSize, query, setQuery]);

  const handlePageChange = (nextPage: number) => {
    setQuery({ ...query, page: nextPage });
  };

  const handleFilterClick = (newFilterState: ResourceFinderFilterQuery) => {
    setQuery({ ...query, ...newFilterState, page: 1 });
  };

  const handleExportClick = async () => {
    try {
      setLoading((prevLoading) => ({ ...prevLoading, export: true }));

      const exportResponse = await userService.exportPeopleFinder(query);

      const link = document.createElement('a');
      link.href = URL.createObjectURL(exportResponse.data);
      link.download = 'leapfroggers skills.csv';
      link.click();

      setTimeout(() => URL.revokeObjectURL(link.href), 0);
    } catch (err) {
      handleError(err as object);
    } finally {
      setLoading((prevLoading) => ({ ...prevLoading, export: false }));
    }
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    setQuery({ ...query, size: nextPageSize, page: 1 });
  };

  const handleClearClick = () => {
    setQuery({ page: 1 });
  };

  return (
    <main>
      <div className="container">
        <div className="full-scope-card">
          <div className="full-scope-card__header table-header name-wrap">
            <div className="d-flex flex-row">
              <h3 className="table-title">People Finder</h3>
            </div>
          </div>
          <div className="full-scope-card__content d-flex position-relative">
            <div className="resource-filter-area">
              <ResourceFilter
                skills={skills}
                initialState={filterQuery}
                designations={designations}
                areas={areas}
                onFilter={handleFilterClick}
                onClear={handleClearClick}
                loadingSkills={loading.skills}
                loadingDesignations={loading.designations}
                loadingAreas={loading.areas}
              />
            </div>
            <div className="resource-results-area">
              {loading.employees ? (
                <Loading />
              ) : (
                <>
                  <ResourceResults
                    employees={employees}
                    total={total}
                    exporting={loading.export}
                    onExport={handleExportClick}
                  />
                  <Pagination
                    pageData={{ page: +String(page ?? '') }}
                    onPageChange={handlePageChange}
                    pageCount={+(pageCount || MINIMUM_PAGE_COUNT)}
                    onPageSizeChange={handlePageSizeChange}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ResourceFinder;
